import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Save, Plus, Trash2, User, ArrowLeft } from 'lucide-react';
import { orderService } from '../api/orders';
import { encounterService } from '../api/encounters';
import { patientService } from '../../../lib/services';
import { orderSchema, type OrderFormData } from '../schemas/orderSchema';
import { useStore } from '../../../lib/store';
import { getDoctorIdFromUser, isDoctor } from '../../../lib/rbac';
import toast from 'react-hot-toast';
import type { Patient } from '../../../lib/supabase';
import type { Encounter } from '../types';

const OrderNew: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useStore();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const doctorId = getDoctorIdFromUser(user);
  const patientId = searchParams.get('patientId');
  const encounterId = searchParams.get('encounterId');

  const {
    register,
    handleSubmit,
    control,
  } = useForm<OrderFormData>({
    resolver: zodResolver(orderSchema),
    defaultValues: {
      type: 'lab',
      items: [{ name: '', code: '' }],
      priority: 'normal',
      notes: '',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const [selectedPatientId, setSelectedPatientId] = useState<string>('');

  useEffect(() => {
    if (!isDoctor(user)) {
      toast.error('Access denied. Doctor privileges required.');
      navigate('/');
      return;
    }
    
    if (!doctorId) {
      toast.error('Doctor ID not found. Please contact administrator.');
      navigate('/');
      return;
    }

    loadData();
  }, [user, doctorId, navigate]);

  useEffect(() => {
    if (selectedPatientId) {
      loadPatientEncounters(selectedPatientId);
    }
  }, [selectedPatientId]);

  const loadData = async () => {
    try {
      const [patientsData, encountersData] = await Promise.all([
        patientService.getAll(undefined, user),
        encounterService.getAll(),
      ]);
      
      setPatients(patientsData);
      
      const doctorEncounters = encountersData.filter(enc => enc.doctorId === doctorId);
      setEncounters(doctorEncounters);

      if (patientId) {
        const patient = patientsData.find((p: any) => p.id === patientId);
        if (patient) {
          setSelectedPatient(patient);
          setSelectedPatientId(patientId);
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load patient information');
    } finally {
      setLoading(false);
    }
  };

  const loadPatientEncounters = async (selectedPatientId: string) => {
    try {
      const allEncounters = await encounterService.getAll();
      const patientEncounters = allEncounters.filter(
        enc => enc.patientId === selectedPatientId && enc.doctorId === doctorId
      );
      setEncounters(patientEncounters);
      
      const patient = patients.find(p => p.id === selectedPatientId);
      if (patient) {
        setSelectedPatient(patient);
      }
    } catch (error) {
      console.error('Failed to load encounters:', error);
    }
  };

  const onSubmit = async (data: OrderFormData) => {
    const currentPatientId = patientId || selectedPatientId;
    if (!currentPatientId) {
      toast.error('Please select a patient');
      return;
    }

    if (!encounterId && encounters.length === 0) {
      toast.error('Please select a patient with an active encounter');
      return;
    }

    const targetEncounterId = encounterId || encounters[0]?.id;
    if (!targetEncounterId) {
      toast.error('No encounter found for this patient');
      return;
    }

    setSaving(true);
    try {
      await orderService.create(targetEncounterId, data);
      toast.success('Order created successfully');
      navigate(`/orders`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to create order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/orders')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">New Lab Order</h1>
            <p className="text-gray-600">Create a new lab or radiology order</p>
          </div>
        </div>
      </div>

      {!patientId && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Select Patient</h2>
          <select
            value={selectedPatientId}
            onChange={(e) => setSelectedPatientId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select a patient</option>
            {patients.map((patient) => (
              <option key={patient.id} value={patient.id}>
                {patient.name} - {patient.email}
              </option>
            ))}
          </select>
          {!selectedPatientId && encounters.length === 0 && (
            <p className="mt-1 text-sm text-red-600">Please select a patient</p>
          )}
        </div>
      )}

      {selectedPatient && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center space-x-4">
            <User className="h-12 w-12 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold">{selectedPatient.name}</h3>
              <p className="text-sm text-gray-600">{selectedPatient.email}</p>
              <p className="text-sm text-gray-600">{selectedPatient.phone}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Order Type</h2>
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                value="lab"
                {...register('type')}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <div>
                <div className="font-medium text-gray-900">Laboratory</div>
                <div className="text-sm text-gray-500">Blood tests, urine analysis, etc.</div>
              </div>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                value="radiology"
                {...register('type')}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <div>
                <div className="font-medium text-gray-900">Radiology</div>
                <div className="text-sm text-gray-500">X-ray, CT scan, MRI, etc.</div>
              </div>
            </label>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Test Items</h2>
            <button
              type="button"
              onClick={() => append({ name: '', code: '' })}
              className="flex items-center space-x-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              <span>Add Item</span>
            </button>
          </div>
          
          <div className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.id} className="flex items-center space-x-4">
                <div className="flex-1">
                  <input
                    {...register(`items.${index}.name`)}
                    placeholder="Test name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="w-32">
                  <input
                    {...register(`items.${index}.code`)}
                    placeholder="Code"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="p-2 text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Additional Details</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                {...register('priority')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                {...register('notes')}
                rows={3}
                placeholder="Additional notes or instructions"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/orders')}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Creating...' : 'Create Order'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrderNew;
